import { pointsFor, type Colour } from '../model/types.js';
import {
  PairingError,
  type Pair,
  type PairingEngine,
  type PairingPlayer,
  type PairingRequest,
  type PairingResult,
} from './types.js';

/**
 * Standard bracket order for a power-of-two field: seeds 1 and 2 can meet
 * only in the final, 1-4 only in the semi-finals, and so on.
 * bracketOrder(8) = [1, 8, 4, 5, 2, 7, 3, 6].
 */
export function bracketOrder(size: number): number[] {
  let order = [1];
  while (order.length < size) {
    const n = order.length * 2;
    order = order.flatMap((s) => [s, n + 1 - s]);
  }
  return order;
}

interface Match {
  readonly a: number;
  readonly b: number | null;
}

/**
 * Knockout: the field is seeded by pairing number into a bracket (byes go
 * to the top seeds when the field is not a power of two). Each match is
 * `gamesPerMatch` games with alternating colours; a match still level after
 * them continues with single play-off games (rapid, blitz, armageddon as the
 * rules of the event say) until one player is ahead. Winners advance.
 */
export class KnockoutEngine implements PairingEngine {
  readonly id = 'knockout';
  readonly name = 'Knockout';

  constructor(private readonly gamesPerMatch = 1) {}

  pair(request: PairingRequest): PairingResult {
    const players = [...request.players].sort((x, y) => x.id - y.id);
    const byId = new Map(players.map((p) => [p.id, p]));
    const size = 2 ** Math.ceil(Math.log2(Math.max(2, players.length)));
    const slots = bracketOrder(size).map((seed) =>
      seed <= players.length ? (players[seed - 1] as PairingPlayer).id : null,
    );
    let matches: Match[] = [];
    for (let i = 0; i < slots.length; i += 2) {
      const a = slots[i] as number | null;
      const b = slots[i + 1] as number | null;
      if (a === null && b === null) continue;
      matches.push(a === null ? { a: b as number, b: null } : { a, b });
    }
    // Replay the history stage by stage to find the current stage.
    let roundIndex = 0;
    const played = request.round - 1;
    const ps = request.pointSystem;
    for (;;) {
      const live = matches.filter((m) => m.b !== null);
      if (live.length === 0 && matches.length <= 1) {
        throw new PairingError('the knockout is finished');
      }
      // Work out each live match from the rounds since the stage began.
      const status = live.map((m) => {
        let pa = 0;
        let pb = 0;
        let games = 0;
        for (let r = roundIndex; r < played; r++) {
          const e = byId.get(m.a)?.history[r];
          if (e === undefined || e.opponent !== m.b) continue;
          games++;
          pa += pointsFor(e.result, ps);
          const f = byId.get(m.b as number)?.history[r];
          pb += f === undefined ? 0 : pointsFor(f.result, ps);
        }
        const done = games >= this.gamesPerMatch && pa !== pb;
        return { m, pa, pb, games, done };
      });
      if (status.every((s) => s.done)) {
        // Advance winners (and bye holders) to the next stage.
        const winners = matches.map((m) => {
          if (m.b === null) return m.a;
          const s = status.find((x) => x.m === m) as (typeof status)[number];
          return s.pa > s.pb ? m.a : (m.b as number);
        });
        if (winners.length === 1) throw new PairingError('the knockout is finished');
        roundIndex += Math.max(...status.map((s) => s.games), 0);
        const next: Match[] = [];
        for (let i = 0; i < winners.length; i += 2) {
          next.push({ a: winners[i] as number, b: (winners[i + 1] as number | undefined) ?? null });
        }
        matches = next;
        continue;
      }
      // Pair every undecided match for one more game; colours alternate.
      const pairs: Pair[] = [];
      for (const s of status) {
        if (s.done) continue;
        const a = byId.get(s.m.a) as PairingPlayer;
        const last = lastColour(a, roundIndex, played);
        const aWhite: boolean = last === null ? s.games % 2 === 0 : last === 'b';
        pairs.push(
          aWhite
            ? { white: s.m.a, black: s.m.b as number }
            : { white: s.m.b as number, black: s.m.a },
        );
      }
      return { pairs, byes: [], notes: [] };
    }
  }
}

function lastColour(p: PairingPlayer, from: number, to: number): Colour | null {
  for (let r = to - 1; r >= from; r--) {
    const c = p.history[r]?.colour;
    if (c !== null && c !== undefined) return c;
  }
  return null;
}
